
import { useState } from 'react';
import { supabase } from '../../../supabaseClient';

interface UploadClipProps {
  onClose: () => void;
  onSuccess?: () => void;
}

const LEGENDS = [
  'Bodvar', 'Cassidy', 'Orion', 'Lord Vraxx', 'Gnash', 'Queen Nai', 'Hattori', 'Sir Roland',
  'Scarlet', 'Thatch', 'Ada', 'Sentinel', 'Lucien', 'Teros', 'Brynn', 'Asuri', 'Barraza',
  'Ember', 'Azoth', 'Koji', 'Ulgrim', 'Diana', 'Jhala', 'Kor', 'Wu Shang', 'Val', 'Ragnir',
  'Cross', 'Mirage', 'Nix', 'Mordex', 'Yumiko', 'Artemis', 'Caspian', 'Sidra', 'Xull',
  'Kaya', 'Isaiah', 'Jiro', 'Lin Fei', 'Zariel', 'Rayman', 'Dusk', 'Fait', 'Thor', 'Petra',
  'Vector', 'Volkov', 'Onyx', 'Jaeyun', 'Mako', 'Magyar', 'Reno', 'Munin', 'Arcadia', 'Ezio',
  'Tezca', 'Thea', 'Red Raptor', 'Loki'
];

const CLIP_TYPES = [
  { value: 'highlight', label: 'Highlight' },
  { value: 'combo', label: 'Combo' },
  { value: 'clutch', label: 'Clutch Play' },
  { value: 'funny', label: 'Funny Moment' },
  { value: 'tutorial', label: 'Tutorial' },
  { value: 'montage', label: 'Montage' }
];

export default function UploadClip({ onClose, onSuccess }: UploadClipProps) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    clip_type: 'highlight',
    legend_used: '',
    tags: ''
  });
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    setUploadProgress(0);

    try {
      // Check if user is authenticated
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('You must be logged in to upload clips');
        setIsSubmitting(false);
        return;
      }

      if (!videoFile) {
        setError('Please select a video file');
        setIsSubmitting(false);
        return;
      }

      // Validate file size (max 500MB)
      if (videoFile.size > 500 * 1024 * 1024) {
        setError('Video file must be less than 500MB');
        setIsSubmitting(false);
        return;
      }

      setUploadProgress(10);

      // Generate unique filename
      const timestamp = Date.now();
      const videoExt = videoFile.name.split('.').pop();
      const videoFileName = `${user.id}/${timestamp}.${videoExt}`;

      // Upload video to Supabase Storage
      setUploadProgress(20);
      const { error: videoUploadError } = await supabase.storage
        .from('clips')
        .upload(videoFileName, videoFile, {
          cacheControl: '3600',
          upsert: false
        });

      if (videoUploadError) {
        throw new Error(`Video upload failed: ${videoUploadError.message}`);
      }

      setUploadProgress(60);

      // Get video public URL
      const { data: videoUrlData } = supabase.storage
        .from('clips')
        .getPublicUrl(videoFileName);

      let thumbnailUrl = null;

      // Upload thumbnail if provided
      if (thumbnailFile) {
        const thumbExt = thumbnailFile.name.split('.').pop();
        const thumbFileName = `${user.id}/thumb_${timestamp}.${thumbExt}`;

        const { error: thumbUploadError } = await supabase.storage
          .from('clips')
          .upload(thumbFileName, thumbnailFile, {
            cacheControl: '3600',
            upsert: false
          });

        if (!thumbUploadError) {
          const { data: thumbUrlData } = supabase.storage
            .from('clips')
            .getPublicUrl(thumbFileName);
          thumbnailUrl = thumbUrlData.publicUrl;
        }
      }

      setUploadProgress(80);

      // Parse tags
      const tagsArray = formData.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      // Insert clip record into database
      const { error: insertError } = await supabase
        .from('clips')
        .insert({
          user_id: user.id,
          title: formData.title,
          description: formData.description || null,
          clip_url: videoUrlData.publicUrl,
          thumbnail_url: thumbnailUrl,
          duration: 0, // Could be extracted from video metadata
          views: 0,
          likes: 0,
          legend_used: formData.legend_used || null,
          clip_type: formData.clip_type,
          tags: tagsArray.length > 0 ? tagsArray : null
        });

      if (insertError) {
        throw new Error(`Failed to save clip: ${insertError.message}`);
      }

      setUploadProgress(100);
      setSubmitSuccess(true);

      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 2000);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setIsSubmitting(false);
    }
  };

  const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (!file.type.startsWith('video/')) {
        setError('Please select a valid video file');
        return;
      }
      setVideoFile(file);
      setError(null);
    }
  };

  const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file for thumbnail');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('Thumbnail must be less than 5MB');
        return;
      }
      setThumbnailFile(file);
      setThumbnailPreview(URL.createObjectURL(file));
      setError(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white">Upload Your Clip</h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors cursor-pointer w-10 h-10 flex items-center justify-center"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {submitSuccess ? (
          <div className="p-12 text-center">
            <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-check-line text-white text-4xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">Clip Uploaded!</h3>
            <p className="text-gray-400">
              Your clip has been uploaded successfully and is now live for the community to see!
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6">
            {error && (
              <div className="mb-6 bg-red-600/20 border border-red-600 rounded-lg p-4 flex items-center">
                <i className="ri-error-warning-line text-red-500 text-xl mr-3"></i>
                <p className="text-red-400">{error}</p>
              </div>
            )}

            <div className="mb-6">
              <label className="block text-white font-semibold mb-2">
                Clip Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Insane 0-to-Death Combo"
                className="w-full bg-gray-900 text-white px-4 py-3 rounded-lg border border-gray-700 focus:border-red-600 focus:outline-none text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-white font-semibold mb-2">
                  Clip Type <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={formData.clip_type}
                  onChange={(e) => setFormData({ ...formData, clip_type: e.target.value })}
                  className="w-full bg-gray-900 text-white px-4 py-3 rounded-lg border border-gray-700 focus:border-red-600 focus:outline-none text-sm cursor-pointer"
                >
                  {CLIP_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-white font-semibold mb-2">
                  Legend Used
                </label>
                <select
                  value={formData.legend_used}
                  onChange={(e) => setFormData({ ...formData, legend_used: e.target.value })}
                  className="w-full bg-gray-900 text-white px-4 py-3 rounded-lg border border-gray-700 focus:border-red-600 focus:outline-none text-sm cursor-pointer"
                >
                  <option value="">Select Legend</option>
                  {LEGENDS.map(legend => (
                    <option key={legend} value={legend}>{legend}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-white font-semibold mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe your clip..."
                maxLength={500}
                rows={3}
                className="w-full bg-gray-900 text-white px-4 py-3 rounded-lg border border-gray-700 focus:border-red-600 focus:outline-none resize-none text-sm"
              />
              <div className="text-right text-gray-500 text-sm mt-1">
                {formData.description.length}/500
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-white font-semibold mb-2">
                Tags
              </label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="e.g., combo, ranked, diamond (comma separated)"
                className="w-full bg-gray-900 text-white px-4 py-3 rounded-lg border border-gray-700 focus:border-red-600 focus:outline-none text-sm"
              />
              <p className="text-gray-500 text-sm mt-1">Separate tags with commas</p>
            </div>

            <div className="mb-6">
              <label className="block text-white font-semibold mb-2">
                Video File <span className="text-red-500">*</span>
              </label>
              <div className="border-2 border-dashed border-gray-700 rounded-lg p-8 text-center hover:border-red-600 transition-colors">
                <input
                  type="file"
                  required
                  accept="video/*"
                  onChange={handleVideoChange}
                  className="hidden"
                  id="video-upload"
                />
                <label htmlFor="video-upload" className="cursor-pointer">
                  <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-video-upload-line text-white text-3xl"></i>
                  </div>
                  {videoFile ? (
                    <div>
                      <p className="text-white font-semibold mb-1">{videoFile.name}</p>
                      <p className="text-gray-400 text-sm">
                        {(videoFile.size / (1024 * 1024)).toFixed(2)} MB
                      </p>
                    </div>
                  ) : (
                    <>
                      <p className="text-white font-semibold mb-2">Click to upload video</p>
                      <p className="text-gray-400 text-sm">MP4, MOV, WebM (Max 500MB)</p>
                    </>
                  )}
                </label>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-white font-semibold mb-2">
                Thumbnail (Optional)
              </label>
              <div className="border-2 border-dashed border-gray-700 rounded-lg p-6 text-center hover:border-red-600 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleThumbnailChange}
                  className="hidden"
                  id="thumbnail-upload"
                />
                <label htmlFor="thumbnail-upload" className="cursor-pointer">
                  {thumbnailPreview ? (
                    <div className="relative">
                      <img 
                        src={thumbnailPreview} 
                        alt="Thumbnail preview" 
                        className="max-h-32 mx-auto rounded-lg object-cover"
                      />
                      <p className="text-gray-400 text-sm mt-2">Click to change</p>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-3">
                      <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center">
                        <i className="ri-image-add-line text-gray-400 text-xl"></i>
                      </div>
                      <div className="text-left">
                        <p className="text-white font-medium text-sm">Add custom thumbnail</p>
                        <p className="text-gray-500 text-xs">PNG, JPG (Max 5MB)</p>
                      </div>
                    </div>
                  )}
                </label>
              </div>
            </div>

            {isSubmitting && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white text-sm font-medium">Uploading...</span>
                  <span className="text-red-400 text-sm">{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-red-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}

            <div className="flex gap-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !videoFile}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Uploading...
                  </>
                ) : (
                  <>
                    <i className="ri-upload-2-line mr-2"></i>
                    Upload Clip
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
